/**
 * The one hotkey registry (scope §10 lib/keys.ts). The key sheet (`?`), the palette's chords and
 * every tooltip read from here, so a key is documented exactly once. Bindings go through
 * `useKey` from @patchbay/ui, which never fires inside inputs or the editor.
 */
export interface KeyDef {
  id: string
  /** What the user sees on the key cap ("g q", "/", "mod+k"). */
  keys: string
  /** The react-hotkeys-hook combo when it differs from the label. */
  bind?: string
  /**
   * Match the produced character, not the physical key, whatever modifier made it: "?" is Shift+/ on a US
   * layout but Shift+ß on a German one. Used for punctuation keys.
   */
  matchKey?: boolean
  label: string
  scope: 'global' | 'queue' | 'case' | 'board' | 'card'
}

export const KEYS = {
  palette: { id: 'palette', keys: 'mod+k', label: 'Command palette', scope: 'global' },
  search: { id: 'search', keys: '/', matchKey: true, label: 'Search', scope: 'global' },
  goHome: { id: 'goHome', keys: 'g h', label: 'Go to Home', scope: 'global' },
  goQueue: { id: 'goQueue', keys: 'g q', label: 'Go to Queue', scope: 'global' },
  goDispatch: { id: 'goDispatch', keys: 'g d', label: 'Go to Schedule', scope: 'global' },
  goLedger: { id: 'goLedger', keys: 'g l', label: 'Go to Ledger', scope: 'global' },
  goBilling: { id: 'goBilling', keys: 'g b', label: 'Go to Ready to bill', scope: 'global' },
  goKnowledge: { id: 'goKnowledge', keys: 'g k', label: 'Go to Knowledge', scope: 'global' },
  // `g r` is the case sidebar's Riley tab (railRiley), so Reports takes `g e` (rEports) to avoid a double fire.
  goReports: { id: 'goReports', keys: 'g e', label: 'Go to Reports', scope: 'global' },
  goProfit: { id: 'goProfit', keys: 'g p', label: 'Go to Profitability (manager, billing)', scope: 'global' },
  // #1388: Projects portfolio (manager, cover, billing). `g j` is free; slice 85 binds it in global-keys.tsx.
  goProjects: { id: 'goProjects', keys: 'g j', label: 'Go to Projects (manager, cover, billing)', scope: 'global' },
  newCase: { id: 'newCase', keys: 'g n', label: 'New case', scope: 'global' },
  keySheet: { id: 'keySheet', keys: '?', matchKey: true, label: 'Keyboard shortcuts', scope: 'global' },
  toggleRail: { id: 'toggleRail', keys: 'alt+r', label: 'Toggle Riley rail', scope: 'global' },
  rileyFull: { id: 'rileyFull', keys: 'shift+r', label: 'Riley full-size', scope: 'global' },

  down: { id: 'down', keys: 'j', label: 'Next row', scope: 'queue' },
  up: { id: 'up', keys: 'k', label: 'Previous row', scope: 'queue' },
  first: { id: 'first', keys: 'home', label: 'First row', scope: 'queue' },
  last: { id: 'last', keys: 'end', label: 'Last row', scope: 'queue' },
  peek: { id: 'peek', keys: 'p', label: 'Peek selected row', scope: 'queue' },
  open: { id: 'open', keys: 'enter', label: 'Open case', scope: 'queue' },
  expand: { id: 'expand', keys: 'space', label: 'Expand row (last turn + draft)', scope: 'queue' },
  assign: { id: 'assign', keys: 'a', label: 'Take / assign', scope: 'queue' },
  nextMove: { id: 'nextMove', keys: 'n', label: 'Set next move', scope: 'queue' },
  snooze: { id: 'snooze', keys: 's', label: 'Snooze until activity or time', scope: 'queue' },
  link: { id: 'link', keys: 'l', label: 'Link into a case', scope: 'queue' },
  nudge: { id: 'nudge', keys: 'shift+p', label: 'Follow up with the next-move owner (Overdue by technician)', scope: 'queue' },
  dispatch: { id: 'dispatch', keys: 'd', label: 'Schedule', scope: 'case' },
  caseClose: { id: 'caseClose', keys: 'c', label: 'Close case', scope: 'case' },
  caseSnooze: { id: 'caseSnooze', keys: 's', label: 'Snooze case', scope: 'case' },
  time: { id: 'time', keys: 't', label: 'Log time', scope: 'case' },
  update: { id: 'update', keys: 'u', label: 'Update case (status, note, time, next move)', scope: 'case' },

  tab1: { id: 'tab1', keys: '1', label: 'Customer tab', scope: 'case' },
  tab2: { id: 'tab2', keys: '2', label: 'Second audience tab', scope: 'case' },
  tab3: { id: 'tab3', keys: '3', label: 'Third audience tab', scope: 'case' },
  compose: { id: 'compose', keys: 'r', label: 'Reply', scope: 'case' },
  toggleTurns: { id: 'toggleTurns', keys: 'shift+e', label: 'Expand all / collapse all turns', scope: 'case' },
  send: { id: 'send', keys: 'mod+enter', label: 'Send (guard re-checks the final body)', scope: 'case' },
  prevCase: { id: 'prevCase', keys: '[', bind: 'bracketleft', label: 'Previous case in lane', scope: 'case' },
  nextCase: { id: 'nextCase', keys: ']', bind: 'bracketright', label: 'Next case in lane', scope: 'case' },
  back: { id: 'back', keys: 'escape', label: 'Back to queue', scope: 'case' },
  caseLink: { id: 'caseLink', keys: 'l', label: 'Link a ticket to this case', scope: 'case' },
  caseObligations: { id: 'caseObligations', keys: 'o', label: 'Obligations (add / dismiss)', scope: 'case' },
  caseWorkOrders: { id: 'caseWorkOrders', keys: 'w', label: 'Work orders', scope: 'case' },
  investigate: { id: 'investigate', keys: 'i', label: 'Investigate (Riley, read-only)', scope: 'case' },
  // #273: the case sidebar's tabs (1-4 also work with focus inside the sidebar). Details is `g i` because `g d` is
  // already Go to Schedule everywhere.
  railRiley: { id: 'railRiley', keys: 'g r', label: 'Sidebar: Riley', scope: 'case' },
  railDetails: { id: 'railDetails', keys: 'g i', label: 'Sidebar: Details', scope: 'case' },
  railWork: { id: 'railWork', keys: 'g w', label: 'Sidebar: Work', scope: 'case' },
  railActivity: { id: 'railActivity', keys: 'g a', label: 'Sidebar: Activity', scope: 'case' },
  // #1388: the project's Plan tab (home case only; slice 86 binds it in case-rail-tabs.tsx).
  railPlan: { id: 'railPlan', keys: 'g t', label: 'Sidebar: Plan (project)', scope: 'case' },

  cardConfirm: { id: 'cardConfirm', keys: 'a', label: 'Confirm focused card', scope: 'card' },
  cardEdit: { id: 'cardEdit', keys: 'e', label: 'Edit focused card', scope: 'card' },
  cardDismiss: { id: 'cardDismiss', keys: 'x', label: 'Dismiss focused card', scope: 'card' },

  colLeft: { id: 'colLeft', keys: 'h', label: 'Column left', scope: 'board' },
  colRight: { id: 'colRight', keys: 'l', label: 'Column right', scope: 'board' },
  approve: { id: 'approve', keys: 'a', label: 'Approve packet', scope: 'board' },
  returnWo: { id: 'returnWo', keys: 'b', label: 'Return packet', scope: 'board' },
} as const satisfies Record<string, KeyDef>

export const KEY_LIST: KeyDef[] = Object.values(KEYS)

/** The combo to bind for a registry entry. */
export const bindOf = (k: KeyDef): string => k.bind ?? k.keys

/** Hook options for a registry entry (punctuation matches the produced character). */
export const bindOptions = (k: KeyDef): { useKey?: boolean; ignoreModifiers?: boolean } =>
  k.matchKey ? { useKey: true, ignoreModifiers: true } : {}
